import { PieArcDatum } from 'd3';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  LocalFilterOptions,
  ResetFilterOptions,
  SaveFilterParams,
} from 'src/components/organisms';
import {
  TimeframeInput,
  useGetIssueCategoriesByIntakeMethodGraphQuery,
} from 'src/graphql';
import * as Types from 'src/types';
import {
  InsightsDashboard,
  LocationFilterCategory,
  REPORT_SOURCE,
  State,
} from 'src/types';
import {
  getOrderedColor,
  sortDataArrayByNumber,
  sortDataArrayByPercentage,
  sortDataArrayByString,
} from 'src/utils';
import { CHART_IDS, trackChartRedrawn } from 'src/utils/analytics';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';
import { getYear } from 'src/utils/time';
import { getColumnNames } from '../helpers';
import { flattenDonutAndBarGraphData } from '../utils/flattenDonutAndBarGraphData';
import { ColumnSortType, TableDataType } from './useCasesOverTime';

const tableColumns = [
  { label: 'Intake method' },
  { label: 'Issue category name' },
  { label: 'Issue category count' },
  { label: 'Intake method percentage' },
];

interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
  organisationData: {
    sites?: string[];
    countries?: string[];
    departments?: string[];
    oldestReportSubmittedAtDate: Date;
    categories?: string[];
  };
}
export type REPORT_SOURCE_TYPES = keyof typeof REPORT_SOURCE;

export const useIssueCategoriesByIntakeMethod = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
  organisationData,
}: Parameters) => {
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.OPERATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [selectedDepartments, setSelectedDepartments] = useState(
    organisationData.departments ?? [],
  );
  const [selectedCategories, setSelectedCategories] = useState(
    organisationData.categories ?? [],
  );
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [localLocationFilter, setLocalLocationFilter] = useState(
    currentGlobalLocationFilter,
  );
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [selectedSegment, setSelectedSegment] = useState({
    intakeMethod: '',
    color: '',
  });
  const [displayTableData, setDisplayTableData] = useState<TableDataType[]>([]);
  const [storedTableData, setStoredTableData] = useState<TableDataType[]>([]);
  const [sortedColumn, setSortedColumn] = useState<ColumnSortType>({
    columnName: 'Intake method',
    direction: 'asc',
  });
  const [categoryAxisLabelCount, setCategoryAxisLabelCount] =
    useState<number>(0);
  const [pdfIsDownloading, setPdfIsDownloading] = useState<boolean>(false);

  const queryLocationVariables = buildLocationQueryVariables({
    locationFilter: localLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountryCodes,
    caseRegionIds: selectedCaseRegionIds,
  });

  const { data, isLoading } = useGetIssueCategoriesByIntakeMethodGraphQuery({
    timeframe,
    ...queryLocationVariables,
    departments: selectedDepartments,
    categories: selectedCategories,
  });
  const graphData = data?.getIssueCategoriesByIntakeMethodGraph;
  const barChartData = data?.getIssueCategoriesByIntakeMethodGraph.data.find(
    (d) =>
      REPORT_SOURCE[d.donutSegment as REPORT_SOURCE_TYPES] ===
      selectedSegment.intakeMethod,
  );
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  let issueCategoriesByIntakeMethodList: string[] = useMemo(() => [], []);

  useEffect(() => {
    setSelectedSites(globalFilterSites);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterSites, currentGlobalLocationFilter]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterCountries, currentGlobalLocationFilter]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  useEffect(() => {
    if (organisationData?.departments?.length && !selectedDepartments.length) {
      setSelectedDepartments(organisationData.departments);
    }
  }, [organisationData, selectedDepartments]);

  useEffect(() => {
    if (!selectedCategories.length && organisationData.categories?.length) {
      setSelectedCategories(organisationData.categories);
    }
  }, [organisationData.categories, selectedCategories.length]);

  const isCaseMode =
    storedGlobalFilters.locationFilterCategory === LocationFilterCategory.CASE;

  const localFilterOptions: LocalFilterOptions = {
    ...(isCaseMode
      ? {}
      : {
          site: {
            options: organisationData.sites ?? [],
            selectedOptions: selectedSites,
          },
          country: {
            options: organisationData.countries ?? [],
            selectedOptions: selectedCountries,
          },
          selectedLocationFilter: localLocationFilter,
        }),
    time: {
      timeframe,
      oldestSelectableYear: getYear(
        organisationData.oldestReportSubmittedAtDate,
      ),
    },
    department: {
      options: organisationData.departments ?? [],
      selectedOptions: selectedDepartments ?? [],
    },
    category: {
      options: organisationData.categories ?? [],
      selectedOptions: selectedCategories,
    },
  };

  const saveLocalFilters = (params: SaveFilterParams) => {
    if (params.selectedSites) {
      setSelectedSites(params.selectedSites);
    }
    if (params.selectedCountries) {
      setSelectedCountries(params.selectedCountries);
    }
    if (params.selectedDepartments) {
      setSelectedDepartments(params.selectedDepartments);
    }
    if (params.selectedTimeframe) {
      setTimeframe(params.selectedTimeframe);
    }
    if (params.selectedCategories) {
      setSelectedCategories(params.selectedCategories);
    }
    if (params.selectedLocationFilter) {
      setLocalLocationFilter(params.selectedLocationFilter);
    }
  };

  const formattedDonutData = useMemo(
    () =>
      graphData?.data.reduce(
        (obj, item) => ({
          ...obj,
          [REPORT_SOURCE[item.donutSegment as REPORT_SOURCE_TYPES]]: item.total,
        }),
        {},
      ),
    [graphData],
  );
  if (formattedDonutData) {
    const keyValuePairs: [string, number][] =
      Object.entries(formattedDonutData);
    issueCategoriesByIntakeMethodList = keyValuePairs.map((pair) => pair[0]);
  }

  const departmentColors = useMemo(
    () =>
      issueCategoriesByIntakeMethodList.map((_, index) =>
        getOrderedColor(index),
      ),
    [issueCategoriesByIntakeMethodList],
  );
  const issueCategoriesByIntakeMethodLegend =
    issueCategoriesByIntakeMethodList.map((item, index) => {
      return {
        color: departmentColors[index],
        text: item,
        active: item === selectedSegment?.intakeMethod,
      };
    });

  const onDonutClick = (d: PieArcDatum<[string, number]>) => {
    if (organisation.organisation && segmentEnabled) {
      trackChartRedrawn(
        organisation.organisation.id,
        'operational',
        CHART_IDS.intake_methods_per_category,
      );
    }
    setSelectedSegment({
      intakeMethod: d.data[0],
      color: getOrderedColor(d.index),
    });
  };

  const onLegendClick = (option: string) => {
    const intakeMethod = issueCategoriesByIntakeMethodLegend.find(
      (item) => item.text === option,
    );
    if (intakeMethod) {
      if (organisation.organisation && segmentEnabled) {
        trackChartRedrawn(
          organisation.organisation.id,
          'operational',
          CHART_IDS.intake_methods_per_category,
        );
      }
      setSelectedSegment({
        intakeMethod: option,
        color: intakeMethod.color,
      });
    }
  };

  useEffect(() => {
    if (graphData) {
      setSelectedSegment({
        intakeMethod:
          REPORT_SOURCE[graphData.data[0].donutSegment as REPORT_SOURCE_TYPES],
        color: getOrderedColor(0),
      });
    }
  }, [graphData]);

  const formattedBarChartData = barChartData?.categories
    .map((c, i) => ({
      bar: i,
      label: c.category,
      value: c.total,
    }))
    .reverse();

  useEffect(() => {
    const tableData = flattenDonutAndBarGraphData(
      graphData?.data ?? [],
      'intake',
    );

    if (!isEqual(tableData, storedTableData)) {
      setStoredTableData(tableData);
    }
    const labelsCount = graphData?.data[0].categories.length ?? 0;
    setCategoryAxisLabelCount(labelsCount);
  }, [graphData, storedTableData]);

  const handleColumnSort = useCallback(
    (columnName: string, direction: 'asc' | 'desc') => {
      const columnIndex = getColumnNames(tableColumns).indexOf(columnName);
      setSortedColumn({ columnName, direction });
      switch (columnName) {
        case 'Intake method':
        case 'Issue category name': {
          const newTableData = sortDataArrayByString(
            storedTableData,
            columnIndex,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        case 'Issue category count': {
          const newTableData = sortDataArrayByNumber(
            storedTableData,
            columnIndex,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        case 'Intake method percentage': {
          const newTableData = sortDataArrayByPercentage(
            storedTableData,
            columnIndex,
            direction,
          );
          setDisplayTableData(newTableData);
          break;
        }
        default: {
          setDisplayTableData(storedTableData);
          break;
        }
      }
    },
    [storedTableData],
  );

  useEffect(() => {
    handleColumnSort(sortedColumn.columnName, sortedColumn.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedColumn.columnName, sortedColumn.direction, storedTableData]);

  const resetLocalFilters: ResetFilterOptions = {
    siteOptions: globalFilterSites,
    countryOptions: globalFilterCountries,
    departmentOptions: organisationData.departments ?? [],
    timeframe: globalFilterTimeframe,
    categoryOptions: organisationData.categories ?? [],
    globalFilterLocation: currentGlobalLocationFilter,
  };

  return {
    centerLabel: graphData?.centerText ?? '',
    donutData: formattedDonutData,
    onDonutClick,
    onLegendClick,
    xTitle: graphData?.xTitle ?? '',
    yTitle: graphData?.yTitle ?? '',
    barData: formattedBarChartData,
    donutLegend: issueCategoriesByIntakeMethodLegend,
    barColor: selectedSegment.color,
    xAxisMax: graphData?.maxCategoryTotal,
    localFilterOptions,
    saveLocalFilters,
    dataIsLoading: isLoading,
    categoriesByIntakeMethod: graphData?.data,
    selectedSegment: selectedSegment.intakeMethod,
    tableColumns,
    tableData: displayTableData,
    handleColumnSort,
    sortedColumn,
    categoryAxisLabelCount,
    pdfIsDownloading,
    setPdfIsDownloading,
    localLocationFilter,
    resetLocalFilters,
  };
};
